/**
 * Order funnel — walks the customer through one lunch order and adds it to the cart.
 *
 * Every priced choice is a real product (sandwich, side, dessert), so Shopify charges them
 * natively and no option in this funnel is upcharged by the Product Options app. The line
 * item properties that ride along are descriptive only.
 */

/*
 * Keys whose chosen value is a real product variant rather than a line item property: the side,
 * the dessert, and each sandwich add-on. Read from the markup (`data-funnel-addon`) instead of
 * being listed here, so adding a new group of paid extras is a Liquid change alone — the old
 * hardcoded ['side', 'dessert'] silently priced anything new at zero.
 */
function addonKeysIn(root) {
  const keys = new Set();
  root.querySelectorAll('[data-funnel-choice][data-funnel-addon]').forEach((choice) => {
    if (choice.dataset.key) keys.add(choice.dataset.key);
  });
  return Array.from(keys);
}

/** "a, b and c" — the same phrasing the recap sentence uses. */
function SandwichListFormat(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

class OrderFunnel extends HTMLElement {
  constructor() {
    super();
    this.state = {};
    this.builderProperties = {};
    this.activeBuilder = null;
    this.currentStep = 0;
  }

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-funnel-step]'));
    this.progressButtons = Array.from(this.querySelectorAll('[data-funnel-progress]'));
    this.backButton = this.querySelector('[data-funnel-back]');
    this.nextButton = this.querySelector('[data-funnel-next]');
    this.submitButton = this.querySelector('[data-funnel-submit]');
    this.errorElement = this.querySelector('[data-funnel-error]');
    this.quantityInput = this.querySelector('[data-funnel-quantity]');
    this.reviewMedia = this.querySelector('[data-funnel-review-media]');
    this.builders = Array.from(this.querySelectorAll('[data-funnel-builder]'));
    this.addonKeys = addonKeysIn(this);

    if (!this.steps.length) return;

    // Each sandwich has its own builder; keep the visible one's choices in the funnel state.
    this.addEventListener('builder:change', this.onBuilderChange.bind(this));

    this.querySelectorAll('[data-funnel-choice]').forEach((choice) => {
      choice.addEventListener('click', this.onChoiceClick.bind(this));
    });

    this.querySelectorAll('[data-funnel-input]').forEach((input) => {
      input.addEventListener('input', this.onInputChange.bind(this));
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.next();
      });
    });

    // `data-funnel-goto` reaches the dataset as `funnelGoto`; reading `goto` gave NaN and left
    // the review step's Change links hiding every step at once.
    this.querySelectorAll('[data-funnel-goto]').forEach((button) => {
      button.addEventListener('click', () => this.goTo(Number(button.dataset.funnelGoto)));
    });

    // Review-step Change buttons edit in place rather than navigating.
    this.editButtons = Array.from(this.querySelectorAll('[data-funnel-edit]'));
    this.editButtons.forEach((button) => {
      button.addEventListener('click', () => this.toggleEditor(Number(button.dataset.funnelEdit), button));
    });
    this.openEditorIndex = null;
    this.editorAnchor = null;

    this.progressButtons.forEach((button) => {
      button.addEventListener('click', () => this.goTo(Number(button.dataset.index)));
    });

    this.applyPreselected();

    /*
     * A repeat arrives through sessionStorage rather than the URL: the configuration is longer
     * than a query string wants to be, and a link a parent might share or bookmark should not
     * silently carry another child's name in it.
     */
    this.seed = window.OrderMemory?.takeSeed() || null;

    this.backButton?.addEventListener('click', () => this.goTo(this.currentStep - 1));
    this.nextButton?.addEventListener('click', () => this.next());
    this.submitButton?.addEventListener('click', () => this.addToCart());
    this.quantityInput?.addEventListener('change', () => {
      this.renderTotal();
      this.renderRecap();
    });

    if (!this.seed || !this.applySeed(this.seed)) this.goTo(0, { focus: false });
  }

  get currentStepElement() {
    return this.steps[this.currentStep];
  }

  /*
   * Selecting a card records the value and leaves the customer where they are. Nothing
   * auto-advances any more: the add-ons are deselectable, the sandwich step is three groups
   * deep, and the delivery step holds the calendar — in every case moving on by itself would
   * carry someone away from options they had not finished reading. Continue does the moving.
   */
  onChoiceClick(event) {
    const choice = event.currentTarget;
    const key = choice.dataset.key;

    if (choice.hasAttribute('data-funnel-multi')) {
      this.toggleMultiChoice(choice, key);
      return;
    }

    // Deselectable steps (the side) accept "none" as an answer, so a second tap clears it.
    if (choice.hasAttribute('data-funnel-deselectable') && choice.classList.contains('is-selected')) {
      this.clearChoice(key);
      return;
    }

    /*
     * Siblings are matched on the key rather than the surrounding group, because the side
     * cards live in two separate groups ("Add Jimmy Chips®" and "Add Premium Side") yet form
     * one choice — picking a premium side has to clear a chips selection.
     */
    this.querySelectorAll(`[data-funnel-choice][data-key="${CSS.escape(key)}"]`).forEach((sibling) => {
      const selected = sibling === choice;
      sibling.setAttribute('aria-checked', String(selected));
      sibling.classList.toggle('is-selected', selected);
    });

    this.recordChoice(choice);

    this.hideError();
    if (key === 'product') this.showBuilderFor(choice.dataset.value);
    this.updateProgress();
    this.renderTotal();
    if (this.openEditorIndex !== null) this.renderReview();

    /*
     * `data-funnel-stay` is on every card that must not move the customer on: the sandwich
     * cards, so the groups below the fold stay reachable, and the location card, which shares
     * the calendar's step and would otherwise skip the days entirely.
     */
    if (choice.hasAttribute('data-funnel-deselectable') || choice.hasAttribute('data-funnel-stay')) return;

    // Let the selected state paint before sliding to the next step.
    window.setTimeout(() => {
      if (this.currentStepElement?.contains(choice)) this.next();
    }, 200);
  }

  /**
   * Cards that come already chosen — today only the single delivery location — count as answered
   * from the moment the funnel loads, so their property reaches the order whether or not the
   * customer ever touches them. Also run after a reset, which clears every selection.
   */
  applyPreselected() {
    this.querySelectorAll('[data-funnel-choice][data-funnel-preselected]').forEach((choice) => {
      choice.classList.add('is-selected');
      choice.setAttribute('aria-checked', 'true');
      this.recordChoice(choice);
    });
  }

  /** Stores what a single-select card stands for, under the property name it declares. */
  recordChoice(choice) {
    this.state[choice.dataset.key] = {
      value: choice.dataset.value,
      label: choice.dataset.label || choice.dataset.value,
      price: Number(choice.dataset.price) || 0,
    };
  }

  /** Unpicks a deselectable step, leaving it genuinely unanswered. */
  clearChoice(key) {
    this.querySelectorAll(`[data-funnel-choice][data-key="${CSS.escape(key)}"]`).forEach((element) => {
      element.setAttribute('aria-checked', 'false');
      element.classList.remove('is-selected');
    });
    delete this.state[key];
    this.hideError();
    this.updateProgress();
    this.renderTotal();
  }

  /**
   * Delivery dates are pick-as-many, so a tap toggles rather than replaces, and the step does
   * not auto-advance — the customer needs to stay put to choose a second day.
   */
  toggleMultiChoice(choice, key) {
    const selected = !choice.classList.contains('is-selected');
    choice.classList.toggle('is-selected', selected);
    choice.setAttribute('aria-checked', String(selected));

    const chosen = Array.from(
      this.querySelectorAll(`[data-funnel-choice][data-key="${CSS.escape(key)}"].is-selected`)
    );

    if (chosen.length) {
      const labels = chosen.map((element) => element.dataset.label || element.dataset.value);
      this.state[key] = {
        values: chosen.map((element) => element.dataset.value),
        value: chosen.map((element) => element.dataset.value).join(', '),
        // Kept as a list as well as a joined string: the review recap reads the days out one by
        // one ("Thu Aug 20 and Fri Aug 21"), which a pre-joined string cannot be unpicked into.
        labels,
        label: labels.join(', '),
        price: 0,
      };
    } else {
      delete this.state[key];
    }

    this.hideError();
    this.updateProgress();
    this.renderTotal();
    if (this.openEditorIndex !== null) this.renderReview();
  }

  /** The dates chosen, or a single-entry list, so callers can treat both the same way. */
  get deliveryDates() {
    const multi = this.querySelector('[data-funnel-choice][data-funnel-multi]');
    if (!multi) return null;
    return this.state[multi.dataset.key]?.values || [];
  }

  /**
   * The property name the location card declares, e.g. "Delivery Location".
   *
   * Found by its own `data-funnel-location` marker rather than by `data-funnel-stay`, which the
   * sandwich cards now share too — matching on that returned the *sandwich* card, so add-on
   * lines silently lost their school and the cart note read "Deliver to: Plain Ham and Cheese".
   */
  get locationKey() {
    return this.querySelector('[data-funnel-choice][data-funnel-location]')?.dataset.key || null;
  }

  /** The school chosen — today always the only one on offer. */
  get locationLabel() {
    const key = this.locationKey;
    return key ? this.state[key]?.label || null : null;
  }

  /**
   * Reveals the builder belonging to the chosen sandwich and resets the others, so switching
   * sandwiches mid-order never carries a previous sandwich's removals across.
   */
  showBuilderFor(variantId) {
    this.builders.forEach((wrapper) => {
      const matches = wrapper.dataset.variant === String(variantId);
      wrapper.toggleAttribute('hidden', !matches);
      if (!matches) wrapper.querySelector('sandwich-builder')?.reset();
    });

    this.activeBuilder = this.builders
      .find((wrapper) => wrapper.dataset.variant === String(variantId))
      ?.querySelector('sandwich-builder');

    // Seed the state from the freshly shown builder, which already defaults to a bread.
    this.readBuilder();
  }

  onBuilderChange(event) {
    // Ignore builders that are hidden because they belong to another sandwich.
    if (event.target.closest('[data-funnel-builder]')?.hasAttribute('hidden')) return;
    this.readBuilder();
  }

  /** Copies the visible builder's properties into funnel state and refreshes the Build summary. */
  readBuilder() {
    if (!this.activeBuilder) return;

    this.builderProperties = this.activeBuilder.properties;

    const recap = this.activeBuilder.querySelector('[data-builder-recap-text]')?.textContent;
    const bread = this.builderProperties.Bread;
    this.state.build = {
      value: '',
      label: recap || (bread ? `On ${bread}` : 'As described'),
      price: 0,
    };

    this.updateProgress();
  }

  onInputChange(event) {
    const input = event.currentTarget;
    const value = input.value.trim();

    if (value) {
      this.state[input.dataset.key] = { value, label: value, price: 0 };
      this.hideError();
    } else {
      delete this.state[input.dataset.key];
    }

    this.updateProgress();
    if (this.openEditorIndex !== null) this.renderReview();
  }

  next() {
    const step = this.currentStepElement;
    if (!step) return;

    if (step.hasAttribute('data-required') && !this.state[step.dataset.key]) {
      this.showError(this.missingMessage(step));
      return;
    }

    this.hideError();
    if (this.currentStep < this.steps.length - 1) this.goTo(this.currentStep + 1);
  }

  missingMessage(step) {
    return step.dataset.error || 'Please make a selection to continue.';
  }

  goTo(index, { focus = true } = {}) {
    // Integer check included so a bad index can never blank every step at once.
    if (!Number.isInteger(index) || index < 0 || index >= this.steps.length) return;

    // Only allow jumping forward past steps that are already answered.
    if (index > this.currentStep) {
      for (let i = this.currentStep; i < index; i += 1) {
        const step = this.steps[i];
        if (step.hasAttribute('data-required') && !this.state[step.dataset.key]) {
          this.showError(this.missingMessage(step));
          return;
        }
      }
    }

    this.closeEditor();

    this.currentStep = index;
    this.hideError();

    this.steps.forEach((step, stepIndex) => {
      step.toggleAttribute('hidden', stepIndex !== index);
    });

    const isReview = this.currentStepElement.dataset.key === 'review';
    this.backButton?.toggleAttribute('hidden', index === 0);
    this.nextButton?.toggleAttribute('hidden', isReview);
    this.submitButton?.toggleAttribute('hidden', !isReview);

    if (isReview) this.renderReview();
    this.updateProgress();
    // Keeps the calendar's running line right when the customer arrives or steps back onto it.
    this.renderTotal();

    if (focus) {
      this.currentStepElement.querySelector('.order-funnel__step-heading')?.focus({ preventScroll: true });
      this.scrollToTop();
    }
  }

  /*
   * Steps differ wildly in height — the sides step runs past a screen, the student step is one
   * field — so moving on from the bottom of a long step used to leave the customer looking at
   * the bottom of a short one, apparently at nothing. Each step starts at its own top instead.
   *
   * The whole funnel is scrolled to, not just the step, so the progress row stays in frame and
   * the customer can see which step they have landed on. `focus` already ran with preventScroll
   * so this is the only thing moving the page, and it is skipped on the initial render.
   */
  scrollToTop() {
    /*
     * Measured and moved synchronously, right after the step swap. Reading the rectangle forces
     * layout, so the target already accounts for the new step's height.
     *
     * Instant, not smooth, and deliberately not deferred to a frame. A smooth scroll is animated
     * over several frames, and swapping a tall step for a short one shrinks the document mid
     * animation — the browser then clamps the position and abandons the scroll, leaving the
     * customer pinned to the bottom of the page looking at nothing.
     */
    const top = this.getBoundingClientRect().top + window.scrollY - OrderFunnel.SCROLL_OFFSET;
    if (Math.abs(window.scrollY - top) < 2) return;

    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }

  updateProgress() {
    this.progressButtons.forEach((button, index) => {
      const step = this.steps[index];
      const answered = Boolean(step && this.state[step.dataset.key]);
      const isCurrent = index === this.currentStep;

      button.classList.toggle('is-current', isCurrent);
      button.classList.toggle('is-complete', answered && !isCurrent);
      button.disabled = index > this.currentStep && !answered;

      if (isCurrent) {
        button.setAttribute('aria-current', 'step');
      } else {
        button.removeAttribute('aria-current');
      }
    });
  }

  /** The chosen days as human labels ("Thu Aug 20"), in calendar order. */
  get deliveryLabels() {
    const multi = this.querySelector('[data-funnel-choice][data-funnel-multi]');
    if (!multi) return [];
    return this.state[multi.dataset.key]?.labels || [];
  }

  /**
   * The order as a sentence, above the table of rows.
   *
   * The rows answer "is each field right?"; this answers "is this the right order?" — which is
   * the question someone actually has at checkout, and the one a column of labels and values
   * makes you assemble in your head. Both are kept: the sentence to read, the rows to correct.
   *
   * Built from DOM nodes rather than innerHTML because the student's name is free text typed by
   * the customer and must never be parsed as markup.
   */
  renderRecap() {
    const recap = this.querySelector('[data-funnel-recap]');
    if (!recap) return;

    recap.replaceChildren();
    if (!this.state.product) return;

    const text = (value) => document.createTextNode(value);
    const strong = (value) => {
      const element = document.createElement('strong');
      element.textContent = value;
      return element;
    };

    /* "a, b and c" — an Oxford-comma-free list, appended as nodes so items can be emphasised. */
    const appendList = (values, makeNode) => {
      values.forEach((value, index) => {
        if (index > 0) recap.append(text(index === values.length - 1 ? ' and ' : ', '));
        recap.append(makeNode(value));
      });
    };

    // "On 8" French" reads as a sentence of its own; lowercased it joins the one being built.
    const build = this.state.build?.label || '';
    const bread = build ? ` ${build.charAt(0).toLowerCase()}${build.slice(1)}` : '';
    const items = [`${this.state.product.label}${bread}`];
    this.addonKeys.forEach((key) => {
      if (this.state[key]) items.push(this.state[key].label);
    });

    recap.append(strong(this.studentName || 'Your student'), text(' will receive '));
    appendList(items, (value) => text(value));

    const days = this.deliveryLabels;
    if (days.length) {
      recap.append(text(' on '));
      appendList(days, strong);
    }

    // Only one school for now, but the sentence is where a customer would notice a wrong one.
    const location = this.locationLabel;
    if (location) recap.append(text(' at '), strong(location));

    recap.append(text('.'));

    // Only worth saying when it is not the default — one lunch per day needs no explaining.
    if (this.quantity > 1) {
      recap.append(text(` That is ${this.quantity} lunches each day.`));
    }
  }

  /*
   * ---- Editing from the review step -------------------------------------------------------
   *
   * Change lifts the *real* step element into the row rather than rendering a second copy of
   * its controls: same nodes, same listeners, same state, so a sandwich picked here is picked
   * everywhere and there is no second implementation to drift. The step is put back where it
   * came from on close, which is what the comment anchor is for.
   */
  toggleEditor(index, button) {
    if (this.openEditorIndex === index) {
      this.closeEditor();
      return;
    }

    this.closeEditor();

    const step = this.steps[index];
    const slot = button.closest('.order-funnel__review-row')?.querySelector('[data-funnel-editor-slot]');
    if (!step || !slot) return;

    this.editorAnchor = document.createComment('funnel-step');
    step.parentNode.insertBefore(this.editorAnchor, step);
    slot.appendChild(step);
    step.hidden = false;
    slot.hidden = false;

    button.textContent = 'Done';
    button.setAttribute('aria-expanded', 'true');
    this.openEditorIndex = index;

    step.querySelector('.order-funnel__step-heading')?.focus({ preventScroll: true });
    slot.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  closeEditor() {
    if (this.openEditorIndex === null) return;

    const step = this.steps[this.openEditorIndex];
    const slot = step.parentElement;

    step.hidden = true;
    if (this.editorAnchor?.parentNode) {
      this.editorAnchor.parentNode.insertBefore(step, this.editorAnchor);
      this.editorAnchor.remove();
    }
    if (slot?.hasAttribute('data-funnel-editor-slot')) slot.hidden = true;

    const button = this.editButtons.find((b) => Number(b.dataset.funnelEdit) === this.openEditorIndex);
    if (button) {
      button.textContent = 'Change';
      button.setAttribute('aria-expanded', 'false');
    }

    this.editorAnchor = null;
    this.openEditorIndex = null;
    this.renderReview();
  }

  /** Keys for the pick-any extras — one per product, so they cannot be listed statically. */
  get extraKeys() {
    return Array.from(this.querySelectorAll('[data-funnel-choice][data-funnel-extra]'))
      .map((choice) => choice.dataset.key)
      .filter(Boolean);
  }

  renderReview() {
    this.querySelectorAll('[data-funnel-summary]').forEach((element) => {
      element.textContent = this.state[element.dataset.funnelSummary]?.label || '—';
    });

    /*
     * The add-ons row. Its own line rather than folded into Build, because it is the only thing
     * on that step that costs money — a customer scanning the review for what they are paying
     * for should find it next to the side and the dessert.
     */
    const extras = this.querySelector('[data-funnel-summary-extras]');
    if (extras) {
      const chosen = this.extraKeys.map((key) => this.state[key]?.label).filter(Boolean);
      extras.textContent = chosen.length ? SandwichListFormat(chosen) : 'None';
    }

    this.renderRecap();

    if (this.reviewMedia) {
      const selected = this.querySelector('[data-key="product"].is-selected img');
      this.reviewMedia.innerHTML = '';
      if (selected) {
        const image = selected.cloneNode(true);
        image.removeAttribute('sizes');
        image.removeAttribute('loading');
        this.reviewMedia.appendChild(image);
      }
    }
  }

  /** Lunches per delivery day, not per order. */
  get quantity() {
    return Math.max(1, parseInt(this.quantityInput?.value, 10) || 1);
  }

  /** One lunch per chosen day, times the per-day quantity. */
  get totalLunches() {
    const days = this.deliveryDates;
    return this.quantity * Math.max(1, days ? days.length : 1);
  }

  /**
   * What one lunch costs — the sandwich plus whichever add-ons were chosen. Every delivery day
   * buys another one of these, so it is the multiplier behind both totals below.
   */
  get pricePerLunch() {
    // Real product prices arrive in cents from Liquid; the app's option upcharges are dollars.
    const base = (this.state.product?.price || 0) / 100;
    const addons = this.addonKeys.reduce((sum, key) => sum + (this.state[key]?.price || 0), 0) / 100;
    const extras = Object.entries(this.state)
      .filter(([key]) => key !== 'product' && key !== 'build' && !this.addonKeys.includes(key))
      .reduce((sum, [, entry]) => sum + (entry.price || 0), 0);

    return base + addons + extras;
  }

  /* Breathing room above the funnel so it does not sit flush against the viewport edge. */
  static SCROLL_OFFSET = 24;

  static money(amount) {
    return `$${amount.toFixed(2)}`;
  }

  /**
   * Everything one lunch contains, in the order it was chosen. Prices come off the same state
   * entries `pricePerLunch` sums, so the list and the total can never disagree.
   *
   * The sandwich carries the build recap as a note ("On 8" French") rather than a fourth row:
   * it describes the sandwich rather than being another thing in the bag, and it is free.
   */
  get lunchItems() {
    const rows = [];

    if (this.state.product) {
      rows.push({
        label: this.state.product.label,
        note: this.state.build?.label || '',
        price: (this.state.product.price || 0) / 100,
      });
    }

    this.addonKeys.forEach((key) => {
      const entry = this.state[key];
      if (entry) rows.push({ label: entry.label, note: '', price: (entry.price || 0) / 100 });
    });

    return rows;
  }

  /** Redraws the "what's in a lunch" list. Rebuilt wholesale — it is at most three rows. */
  renderLunchItems() {
    const list = this.querySelector('[data-funnel-running-items]');
    if (!list) return;

    list.replaceChildren(
      ...this.lunchItems.map((item) => {
        const row = document.createElement('li');
        row.className = 'order-funnel__running-item';

        const name = document.createElement('span');
        name.className = 'order-funnel__running-name';
        name.textContent = item.label;

        if (item.note) {
          const note = document.createElement('span');
          note.className = 'order-funnel__running-note';
          note.textContent = item.note;
          name.appendChild(note);
        }

        const price = document.createElement('span');
        price.className = 'order-funnel__running-price';
        price.textContent = OrderFunnel.money(item.price);

        row.append(name, price);
        return row;
      })
    );
  }

  renderTotal() {
    const days = this.deliveryDates?.length || 0;
    const lunches = this.totalLunches;
    const perLunch = this.pricePerLunch;
    const total = perLunch * lunches;
    const formatted = OrderFunnel.money(total);
    const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

    const countElement = this.querySelector('[data-funnel-count]');
    if (countElement) {
      countElement.textContent = days
        ? `${days} ${days === 1 ? 'day' : 'days'} × ${this.quantity} = ${lunches} ${
            lunches === 1 ? 'lunch' : 'lunches'
          }`
        : '';
    }

    /*
     * The live line under the calendar. Before any day is tapped it shows the per-lunch price,
     * so the customer knows the rate they are about to multiply; after that it counts the
     * lunches and the money as they go, which is the whole reason the add-on steps now come
     * first — by this point `perLunch` is the real number, not a partial one.
     */
    const runningCount = this.querySelector('[data-funnel-running-count]');
    const runningTotal = this.querySelector('[data-funnel-running-total]');
    const runningCaption = this.querySelector('[data-funnel-running-caption]');
    if (runningCount && runningTotal) {
      this.renderLunchItems();
      if (runningCaption) runningCaption.textContent = perLunch ? 'Each lunch' : '';

      if (!perLunch) {
        runningCount.textContent = '';
        runningTotal.textContent = '';
      } else if (!days) {
        runningCount.textContent = 'Pick your days —';
        runningTotal.textContent = `${OrderFunnel.money(perLunch)} per lunch`;
      } else {
        runningCount.textContent = `${plural(days, 'day')} · ${lunches} ${
          lunches === 1 ? 'lunch' : 'lunches'
        }`;
        runningTotal.textContent = formatted;
      }
    }

    const totalElement = this.querySelector('[data-funnel-total]');
    if (totalElement) totalElement.textContent = formatted;

    /*
     * The price also rides on the button itself. On a phone the sticky bar is often all the
     * customer can see, and "Add to cart · $34.70" answers "what am I about to pay?" without
     * them having to scroll back up to the total.
     */
    const submitText = this.querySelector('.order-funnel__submit-text');
    if (submitText) submitText.textContent = `Add to cart · ${formatted}`;
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    if (!this.errorElement) return;
    this.errorElement.hidden = true;
    this.errorElement.textContent = '';
  }

  setLoading(loading) {
    if (!this.submitButton) return;
    this.submitButton.setAttribute('aria-disabled', String(loading));
    this.submitButton.disabled = loading;
    this.submitButton.querySelector('.order-funnel__spinner')?.toggleAttribute('hidden', !loading);
  }

  async addToCart() {
    const missing = this.steps.find(
      (step) => step.hasAttribute('data-required') && !this.state[step.dataset.key]
    );

    if (missing) {
      this.goTo(this.steps.indexOf(missing));
      this.showError(this.missingMessage(missing));
      return;
    }

    const properties = {};
    Object.entries(this.state).forEach(([key, entry]) => {
      // `product` and the add-ons are variants, and `build` is only a summary — the builder
      // supplies the real property names below.
      if (key === 'product' || key === 'build' || this.addonKeys.includes(key)) return;
      properties[key] = entry.value;
    });
    Object.assign(properties, this.builderProperties || {});

    /*
     * One line per delivery day. Sending a single line with a comma-joined date would leave the
     * kitchen — and the Product Options app, which expects a plain YYYY-MM-DD — unable to tell
     * which lunch belongs to which day.
     */
    const dateKey = this.querySelector('[data-funnel-choice][data-funnel-multi]')?.dataset.key;
    const days = dateKey ? this.deliveryDates : null;
    const addons = this.addonKeys.map((key) => this.state[key]).filter(Boolean);

    /*
     * Add-ons repeat alongside the sandwich, one line per day, carrying the same date, student
     * and school so a picker can see at a glance which bag they belong in. They deliberately do
     * not inherit the sandwich's Bread / Customize properties — those describe the sandwich.
     */
    const addonProperties = {};
    [this.studentNameKey, this.locationKey].forEach((key) => {
      if (key && properties[key]) addonProperties[key] = properties[key];
    });

    const linesFor = (date) => {
      const dated = date ? { [dateKey]: date } : {};
      return [
        { id: this.state.product.value, quantity: this.quantity, properties: { ...properties, ...dated } },
        ...addons.map((addon) => ({
          id: addon.value,
          quantity: this.quantity,
          properties: { ...addonProperties, ...dated },
        })),
      ];
    };

    const items = days?.length ? days.flatMap((date) => linesFor(date)) : linesFor(null);

    this.setLoading(true);

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        this.showError(result.description || result.message || 'Sorry, we could not add this to your cart.');
        this.setLoading(false);
        return;
      }

      await this.noteOrderDetails();

      /*
       * Remembered only now, after the cart has actually accepted the lines — an order that
       * failed to add is not one the customer would want offered back to them.
       */
      window.OrderMemory?.save(this.orderSnapshot);

      if (this.dataset.cartRedirect === 'stay') {
        this.reset();
        this.setLoading(false);
        return;
      }

      window.location = window.routes.cart_url;
    } catch (error) {
      console.error(error);
      this.showError('Sorry, we could not add this to your cart. Please try again.');
      this.setLoading(false);
    }
  }

  /** The property name the free-text step declares, e.g. "Student Name". */
  get studentNameKey() {
    return this.querySelector('[data-funnel-input]')?.dataset.key || null;
  }

  /** Whatever was typed into the free-text step, read via its own declared property name. */
  get studentName() {
    const key = this.studentNameKey;
    return key ? this.state[key]?.value : null;
  }

  /**
   * The student and the school already ride along as line item properties, but those sit
   * collapsed under each line on the order. They are what the kitchen writes on the bag and
   * where the bag is driven to, so they also go on the cart note, which shows at the top of the
   * order in admin and prints on the packing slip.
   *
   * Appended rather than assigned: the customer may have typed their own note, and a second run
   * through the funnel should add a second student instead of overwriting the first. Lines
   * already present are skipped, so ordering twice for the same school notes it once.
   */
  async noteOrderDetails() {
    const lines = [];
    if (this.studentName) lines.push(`Student: ${this.studentName}`);
    if (this.locationLabel) lines.push(`Deliver to: ${this.locationLabel}`);
    if (!lines.length) return;

    try {
      const cartResponse = await fetch(`${window.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      const existing = (cart.note || '').trim();
      const rows = existing.split('\n').map((row) => row.trim());
      const missing = lines.filter((line) => !rows.includes(line));
      if (!missing.length) return;

      const note = [existing, ...missing].filter(Boolean).join('\n');

      await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
    } catch (error) {
      // The lunches are already in the cart; a missing note must not fail the order.
      console.error(error);
    }
  }

  /**
   * Everything needed to rebuild this order except the days — the whole point is that the days
   * are the one thing the customer picks again. Variant ids rather than titles, because a
   * renamed product should still be repeatable; labels ride along only so the "Order again"
   * page has something to print without looking anything up.
   */
  get orderSnapshot() {
    const addons = this.addonKeys
      .map((key) => {
        const entry = this.state[key];
        return entry ? { key, variantId: String(entry.value), label: entry.label } : null;
      })
      .filter(Boolean);

    return {
      id: `o${Date.now()}`,
      savedAt: new Date().toISOString(),
      student: this.studentName || '',
      quantity: this.quantity,
      days: this.deliveryDates?.length || 0,
      product: {
        variantId: String(this.state.product.value),
        label: this.state.product.label,
      },
      build: this.activeBuilder?.snapshot || null,
      addons,
    };
  }

  /** The step holding the calendar — where a repeated order drops the customer. */
  get deliveryStepIndex() {
    return this.steps.findIndex((step) => step.querySelector('[data-funnel-multi]'));
  }

  /**
   * Refills the funnel from a remembered order and jumps to the calendar.
   *
   * Driven by clicking the real cards rather than writing state directly, so a repeat goes
   * through exactly the same code an ordinary order does — the builder swaps, the running total
   * adds up, and a product that has since left the menu simply is not there to click, which is
   * why the whole thing gives up and starts at step one if the sandwich cannot be found.
   */
  applySeed(seed) {
    const sandwich = this.querySelector(
      `[data-funnel-choice][data-key="product"][data-value="${CSS.escape(String(seed.product.variantId))}"]`
    );
    if (!sandwich || sandwich.disabled) return false;

    sandwich.click();
    this.activeBuilder?.restore(seed.build);

    (seed.addons || []).forEach((addon) => {
      const card = this.querySelector(
        `[data-funnel-choice][data-funnel-addon][data-value="${CSS.escape(String(addon.variantId))}"]`
      );
      if (card && !card.disabled) card.click();
    });

    const nameInput = this.querySelector('[data-funnel-input]');
    if (nameInput && seed.student) {
      nameInput.value = seed.student;
      nameInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if (this.quantityInput && seed.quantity > 1) {
      this.quantityInput.value = seed.quantity;
    }

    this.readBuilder();
    this.renderTotal();

    const target = this.deliveryStepIndex;
    this.goTo(target === -1 ? 0 : target, { focus: false });
    return true;
  }

  reset() {
    this.state = {};
    this.builderProperties = {};
    this.activeBuilder = null;
    this.builders.forEach((wrapper) => {
      wrapper.hidden = true;
      wrapper.querySelector('sandwich-builder')?.reset();
    });
    this.querySelectorAll('[data-funnel-choice]').forEach((choice) => {
      choice.classList.remove('is-selected');
      choice.setAttribute('aria-checked', 'false');
    });
    this.querySelectorAll('[data-funnel-input]').forEach((input) => {
      input.value = '';
    });
    if (this.quantityInput) this.quantityInput.value = 1;
    this.applyPreselected();
    this.goTo(0);
  }
}

customElements.define('order-funnel', OrderFunnel);
